import React, {Fragment, useContext, useEffect} from 'react';
import {ApiUtility} from "../common/ApiUtility";
import {OverlayLoadingIndicator} from "../common/OverlayLoadingIndicator";
import {ListPaginationOptions} from "../common/ListPaginationOptions";
import {CreateSubscription} from "./CreateSubscription";
import {GlobalContext, GlobalContextType} from "../admin-frame/AdminFrame";
import {CommonListPageHeader} from "../common/CommonListPageHeader";
import {ConditionalRenderer} from "../common/ConditionalRenderer";
import {useRenderableData} from "../common/hooks/useRenderableData";
import {UrlUtility} from "../common/UrlUtility";
import {MessageDisplayerUtility} from "../common/MessageDisplayerUtility";
import {DateUtility} from "../common/DateUtility";
import {PillButton} from "../common/PillButton";
import {SubscriptionDataUtility} from "../common/SubscriptionDataUtility";
import {InvoiceDataUtility} from "../common/InvoiceDataUtility";
import {SingleInvoice} from "../invoices/SingleInvoice";
import {ConfirmCancelModal} from "../common/ConfirmCancelModal";
import {ReadSubscription} from "./ReadSubscription";
import {RouteComponentProps} from 'react-router-dom';
import {Subscription} from "./SubscriptionTypes";
import {Constants} from "../common/Constants";
import {SelectUtility} from "../common/SelectUtility";
import {SelectOption} from "../common/CommonUIElementTypes";
import Select, {ActionMeta, SingleValue} from 'react-select';
import _ from "lodash";

interface SubscriptionsListPageURLQueryParams {
    page?: number;
    searchText?: string;
    stripeCustomerId?: string;
}

interface SubscriptionsListPageRouteParams {
    subscriptionId: string | undefined;
    invoiceId: string | undefined;
}

export interface SubscriptionsListPageProps extends RouteComponentProps<SubscriptionsListPageRouteParams> {
    showCreateSubscriptionModal: boolean;
}

interface SubscriptionListPageState {
    selectedPage: number;
    searchText: string;
    filterStripeCustomerId?: string;
    showCreateSubscriptionModal: boolean;
    showReadSubscriptionModal: boolean;
    readSubscriptionId?: number;
    showUpdateInvoiceModal: boolean;
    showConfirmSyncModal: boolean;
    updateInvoiceId?: number;
    subscriptionList: [];
    pageCount: number;
    loading: boolean;
    prevUrlQueryString?: string;
    prevParams?: SubscriptionsListPageRouteParams;
    prevShowCreateSubscriptionModal?: boolean;
    stripeCustomerOptions: SelectOption[];
}

export const SubscriptionsListPage = function (props: SubscriptionsListPageProps) {
    const context: GlobalContextType = useContext(GlobalContext);
    const urlBase = '/subscriptions';
    const initialState: SubscriptionListPageState = {
        selectedPage: 1,
        searchText: '',
        filterStripeCustomerId: undefined,
        showCreateSubscriptionModal: false,
        showReadSubscriptionModal: false,
        readSubscriptionId: undefined,
        showUpdateInvoiceModal: false,
        showConfirmSyncModal: false,
        updateInvoiceId: undefined,
        subscriptionList: [],
        pageCount: 0,
        loading: false,
        prevUrlQueryString: undefined,
        prevParams: undefined,
        prevShowCreateSubscriptionModal: undefined,
        stripeCustomerOptions: []
    }
    const renderableData = useRenderableData(initialState)

    // Update state if URL changes.
    if (
        (props.location?.search) !== renderableData.prevUrlQueryString ||
        (props.match?.params) !== renderableData.prevParams ||
        props.showCreateSubscriptionModal !== renderableData.prevShowCreateSubscriptionModal
    ) {
        let selectedPage = 1;
        let searchText = '';
        let filterStripeCustomerId = undefined;
        let readSubscriptionId: string | null = null;
        let showReadSubscriptionModal = false;
        let updateInvoiceId: string | null = null;
        let showUpdateInvoiceModal = false;

        let routeParams = props.match && props.match.params;
        if (
            routeParams?.subscriptionId &&
            routeParams?.subscriptionId !== 'add' &&
            props.match.path.indexOf('/add/') <= -1
        ) {
            showReadSubscriptionModal = true;
            readSubscriptionId = routeParams['subscriptionId'];
        }

        if (routeParams?.invoiceId) {
            showUpdateInvoiceModal = true;
            updateInvoiceId = routeParams.invoiceId;
        }

        let queryParams: SubscriptionsListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
        if (queryParams.page) {
            selectedPage = queryParams.page;
        }
        if (queryParams.searchText) {
            searchText = queryParams.searchText;
        }
        if (queryParams.stripeCustomerId) {
            filterStripeCustomerId = queryParams.stripeCustomerId;
        }

        renderableData.updateAndRender({
            selectedPage: selectedPage,
            searchText: searchText,
            filterStripeCustomerId: filterStripeCustomerId,
            showCreateSubscriptionModal: props.showCreateSubscriptionModal || false,
            showReadSubscriptionModal: showReadSubscriptionModal || false,
            readSubscriptionId: readSubscriptionId,

            showUpdateInvoiceModal: showUpdateInvoiceModal || false,
            updateInvoiceId: updateInvoiceId,

            prevUrlQueryString: props.location?.search,
            prevParams: props.match && props.match.params,
            prevShowCreateSubscriptionModal: props.showCreateSubscriptionModal,
        })
    }

    // Trigger reload of list data on mount and if certain values change in state.
    useEffect(
        function () {
            reloadList();
        },
        [
            '' + renderableData.selectedPage,
            '' + renderableData.searchText,
            '' + renderableData.filterStripeCustomerId
        ]
    );

    function reloadList(callback?: Function) {
        let renderableDataUpdate: Partial<SubscriptionListPageState> = {};
        renderableData.updateAndRender({loading: true})
        ApiUtility.getSubscriptionsList(
            renderableData.selectedPage,
            renderableData.searchText,
            renderableData.filterStripeCustomerId
        )
            .then(function (listData) {
                renderableDataUpdate = {
                    ...listData,
                    loading: false
                }

                return new Promise<void>(function (resolve, reject) {
                    if (renderableData.stripeCustomerOptions.length === 0) {
                        return ApiUtility.getStripeCustomersList()
                            .then(function (stripeCustomersList) {
                                renderableDataUpdate.stripeCustomerOptions = SelectUtility.modelArrayToSelectOptions(
                                    stripeCustomersList, 'id', 'name'
                                )
                                resolve()
                            })
                    } else {
                        resolve()
                    }
                });
            })
            .then(function () {
                renderableData.updateAndRender(renderableDataUpdate);
                if (callback) {
                    callback();
                }
            })
            .catch(function (error) {
                renderableData.updateAndRender({loading: false});
                console && console.error(error);
                MessageDisplayerUtility.error(`An error occurred while loading the subscription list.`);
            });
    }

    function goToAddSubscriptionModal() {
        context.navigator.navigateTo(`${urlBase}/add${props.location.search}`);
    }

    function goToReadSubscriptionModal(subscriptionId: number) {
        context.navigator.navigateTo(`${urlBase}/${subscriptionId}${props.location.search}`);
    }

    function goToUpdateInvoiceModal(invoiceId: number) {
        context.navigator.navigateTo(`${urlBase}/invoices/${invoiceId}${props.location.search}`);
    }

    function goToSubscriptionInvoicesListPage() {
        context.navigator.navigateTo("/subscription-invoices");
    }

    function closeModals() {
        context.navigator.navigateTo(`${urlBase}${props.location.search}`);
    }

    function afterSuccessfulSubscriptionSave(subscriptionId: number) {
        closeModals()
        reloadList(
            () => goToReadSubscriptionModal(subscriptionId)
        );
    }

    function afterSuccessfulInvoiceSave(invoiceId: number) {
        closeModals()
        reloadList(
            () => goToUpdateInvoiceModal(invoiceId)
        );
    }

    function afterSuccessfulInvoiceCancel() {
        closeModals()
        reloadList()
    }

    function filterByStripeCustomerId(option: SelectOption) {
        if (renderableData.filterStripeCustomerId !== option?.value) {
            let queryParams: SubscriptionsListPageURLQueryParams = UrlUtility.getQueryParamsFromProps(props);
            queryParams.stripeCustomerId = option?.value as string || undefined;
            queryParams.page = 1;
            let queryString = UrlUtility.getUrlQueryStringFromQueryParamsObject(queryParams);
            context.navigator.navigateTo(`${urlBase}${queryString}`);
        }
    }

    function onClickSync() {
        renderableData.updateAndRender({showConfirmSyncModal: true})
    }

    function closeConfirmSyncModal() {
        renderableData.updateAndRender({showConfirmSyncModal: false})
    }

    function performSync() {
        renderableData.updateAndRender({loading: true})
        ApiUtility.syncAllSubscriptionInvoices()
            .then((response) => {
                closeConfirmSyncModal();
                MessageDisplayerUtility.success(`Successfully synced subscription invoices.`);
                reloadList();
            })
            .catch((err) => {
                renderableData.updateAndRender({loading: false});
                console && console.error(err);
                MessageDisplayerUtility.error(`An error occurred while syncing the subscription invoices.`);
            });
    }

    function renderSubscriptionsListDisplay(subscriptionsList: Subscription[]) {
        let subscriptionsListDisplay = [];

        for (let i = 0; i < subscriptionsList.length; i++) {
            const subscription = subscriptionsList[i];
            let durationDisplay =
                (DateUtility.dbDateToLocalDisplayDate(subscription.start_date) || 'Unknown') +
                ' - ' +
                (DateUtility.dbDateToLocalDisplayDate(subscription.end_date) || 'Forever')

            let description = subscription.description || '';
            if (description.length > 200) {
                description = description.substring(0, 200) + '...';
            }

            let totalDisplay = SubscriptionDataUtility.getSubscriptionTotalDisplay(subscription)
            let linesDisplay = SubscriptionDataUtility.getSubscriptionLinesDisplay(subscription)

            let invoicesDisplay = [];
            if (subscription.invoices && subscription.invoices.length > 0) {
                invoicesDisplay = [];
                let invoicesLatestFirst = _.sortBy(subscription.invoices, function (invoice) {
                    return -1 * (DateUtility.dbDateToTimestamp('' + invoice.created_at) || 0)
                })
                for (let x = 0; x < invoicesLatestFirst.length; x++) {
                    let invoice = invoicesLatestFirst[x];
                    let invoiceStatusClass = InvoiceDataUtility.invoiceStatusToClass(invoice.invoice_status)
                    let invoiceTitle = SubscriptionDataUtility.getSubscriptionInvoiceTitle(subscription, invoice);

                    invoicesDisplay.push(
                        <div
                            key={`${i + 1}-${x}`}
                            id={`subscriptionListInvoiceLine-${invoice.id}`}
                            className="common-list-sub-row subscription-invoice-list-row"
                            onClick={() => goToUpdateInvoiceModal(invoice.id)}
                        >
                            <div className="common-list-sub-row-content">
                                <div className="subscription-invoice-row-cell label">
                                    {invoiceTitle}
                                </div>
                                <div className="subscription-invoice-row-cell billing-status">
                                    <div className="invoice-status-content">
                                        <div className={`invoice-status-badge ${invoiceStatusClass}`}>
                                            {invoice.invoice_status?.name}
                                        </div>
                                        <ConditionalRenderer
                                            if={invoice.subsequent_auto_sync_failures >= InvoiceDataUtility.getMaxAllowedAutoSyncFailures()}
                                            renderer={() => (
                                                <img
                                                    className="warning-triangle"
                                                    title={Constants.invoiceAutoSyncDisabledMessage}
                                                    src={'/static/images/warning-error-red.svg'}
                                                />
                                            )}
                                        />
                                        <div className="invoice-status-additional-info">
                                            {invoice.invoice_status?.additional_info}
                                        </div>
                                    </div>
                                </div>
                                <div className="subscription-invoice-row-cell icon">
                                    <img
                                        src="/static/images/chevron-right-textcolor.svg"
                                        className="list-row-chevron-icon"
                                    />
                                </div>
                            </div>
                        </div>
                    )
                }
            }

            if (subscription.stripe_only_invoices_info) {
                invoicesDisplay.unshift(
                    <div
                        key={0}
                        className="stripe-only-invoices-info"
                    >
                        {subscription.stripe_only_invoices_info}
                    </div>
                )
            }

            if (invoicesDisplay.length === 0) {
                invoicesDisplay.push(
                    <div key={1} className="no-invoices-for-subscription-message">
                        No invoices found for subscription.
                    </div>
                )
            }

            let listRowClasses = "common-list-row common-list-values-row subscriptions-list-row "
            if (!subscription.active) {
                listRowClasses += "canceled"
            }

            let showAutoSyncDisabledWarning =
                subscription.subsequent_auto_sync_failures >= InvoiceDataUtility.getMaxAllowedAutoSyncFailures()

            subscriptionsListDisplay.push(
                <div
                    key={i + 1}
                    id={`subscriptionId-${subscription.id}`}
                    className={listRowClasses}
                >
                    <div
                        className="common-list-row-main-content"
                        onClick={() => goToReadSubscriptionModal(subscription.id)}
                    >
                        <div className="common-list-row-content">
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell icon"
                            >
                                <img
                                    src="/static/images/invoice-subscription-textcolor.svg"
                                    className="subscription-list-row-icon"
                                />
                            </div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell basics"
                            >
                                <div className="subscription-basics-content">
                                    <div
                                        className={`subscription-basics-top ext-stripe-id-${subscription.ext_stripe_customer_id}`}>
                                        {`${subscription.ext_stripe_customer_name}`}
                                    </div>
                                    <div className="subscription-basics-bottom">
                                        <span className="subscription-invoice-total-display">{totalDisplay}</span>
                                        <span className="subscription-invoice-lines-summary">{linesDisplay}</span>
                                    </div>
                                </div>
                            </div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell description"
                            >{description}</div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell duration"
                            >{durationDisplay}</div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell status"
                            >
                                <div className="subscription-status">{subscription.active ? 'Active' : 'Canceled'}</div>
                                <ConditionalRenderer
                                    if={showAutoSyncDisabledWarning}
                                    renderer={() => (
                                        <img
                                            className="warning-triangle"
                                            title={Constants.subscriptionAutoSyncDisabledMessage}
                                            src={'/static/images/warning-error-red.svg'}
                                        />
                                    )}
                                />
                            </div>
                            <div
                                className="common-list-row-cell common-list-row-value-cell subscriptions-list-row-cell chevron">
                                <img
                                    src="/static/images/chevron-right-textcolor.svg"
                                    className="list-row-chevron-icon"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="common-list-row-sub-content">
                        {invoicesDisplay}
                    </div>
                </div>
            );
        }

        return subscriptionsListDisplay;
    }

    if (renderableData.subscriptionList) {
        return (
            <div className="common-list-page subscriptions-list-page">
                <CommonListPageHeader
                    tabs={[
                        {
                            title: Constants.subscriptionPagesTabs.subscriptions,
                            onClick: () => null,
                            selected: true
                        },
                        {
                            title: Constants.subscriptionPagesTabs.subscriptionInvoices,
                            onClick: goToSubscriptionInvoicesListPage,
                            selected: false
                        },
                    ]}
                    rightHeaderRenderer={() => (
                        <Fragment>
                            <Select
                                placeholder="Filter by customer"
                                className="common-form-select header-filter-select customer-filter-select"
                                classNamePrefix="common-form-select"
                                value={SelectUtility.selectOptionForId(
                                    '' + renderableData.filterStripeCustomerId,
                                    renderableData.stripeCustomerOptions
                                )}
                                onChange={(newValue: SingleValue<SelectOption>, actionMeta: ActionMeta<SelectOption>) => filterByStripeCustomerId(newValue as SelectOption)}
                                options={renderableData.stripeCustomerOptions}
                                isClearable={true}
                            />
                            <PillButton
                                onClick={goToAddSubscriptionModal}
                                containerClasses="add-button-container"
                                buttonClasses="add-button"
                                leftIconSrc={"/static/images/plus-textcolor.svg"}
                                buttonText="ADD"
                            />
                            <PillButton
                                onClick={onClickSync}
                                containerClasses="sync-button-container subscription-list-sync-button-container"
                                buttonClasses="sync-button subscription-list-sync-button"
                                leftIconSrc={"/static/images/sync-white.svg"}
                                buttonText="SYNC ALL"
                            />
                        </Fragment>
                    )}
                />

                <div className="common-list-page-content">
                    <ConditionalRenderer
                        if={renderableData.loading}
                        renderer={() => <OverlayLoadingIndicator/>}
                    />
                    {(() => {
                        if (renderableData.subscriptionList.length > 0) {
                            return (
                                <Fragment>
                                    <div className="common-list-header">
                                        <div className="common-list-row common-list-labels-row">
                                            <div className="common-list-row-content">
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell basics"
                                                >Basics
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell description"
                                                >Description
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell duration"
                                                >Duration
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell status"
                                                >Status
                                                </div>
                                                <div
                                                    className="common-list-row-cell common-list-row-label-cell subscriptions-list-row-cell chevron"
                                                ></div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="common-list-content">
                                        {renderSubscriptionsListDisplay(renderableData.subscriptionList)}
                                    </div>
                                    <ListPaginationOptions
                                        pageCount={renderableData.pageCount}
                                        selectedPage={renderableData.selectedPage}
                                        urlBase="/subscriptions"
                                        location={props.location}
                                    />
                                </Fragment>
                            );
                        } else {
                            return (
                                <div className="common-empty-list-message-container">
                                    <div className="common-empty-list-message">No subscriptions found.</div>
                                </div>
                            );
                        }
                    })()}
                </div>
                {(() => {
                    if (renderableData.showCreateSubscriptionModal) {
                        return (
                            <CreateSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSubscriptionSave}
                            />
                        );
                    } else if (renderableData.showReadSubscriptionModal) {
                        return (
                            <ReadSubscription
                                cancel={closeModals}
                                afterSuccessfulSave={afterSuccessfulSubscriptionSave}
                                subscriptionId={renderableData.readSubscriptionId as number}
                                onClickInvoice={goToUpdateInvoiceModal}
                            />
                        );
                    } else if (renderableData.showUpdateInvoiceModal) {
                        return (
                            <SingleInvoice
                                cancel={closeModals}
                                invoiceId={renderableData.updateInvoiceId}
                                afterSuccessfulSave={afterSuccessfulInvoiceSave}
                                afterSuccessfulCancel={afterSuccessfulInvoiceCancel}
                                goToSubscription={goToReadSubscriptionModal}
                            />
                        );
                    } else if (renderableData.showConfirmSyncModal) {
                        return (
                            <ConfirmCancelModal
                                cancel={closeConfirmSyncModal}
                                confirm={performSync}
                                headerText="Confirm Sync"
                                confirmButtonText="SYNC"
                                warningMessage={[
                                    "Are you sure you want to sync all subscription invoices?",
                                    "This will sync any new Stripe subscription invoices from the last 30 days to our system and QBO."
                                ]}
                            />
                        )
                    }
                })()}
            </div>
        );
    } else {
        return (
            <OverlayLoadingIndicator/>
        );
    }
};
